import type { NodoDeArbol } from '@pasos/schemas';
import { useEffect, useState } from 'react';
import { Check, ICONO, X } from '../../components/ui/iconos.js';
import { PACIENTE } from '../../copy/index.js';

type ArbolProps = {
  arbol: NodoDeArbol;
  /** Reinicia el recorrido cuando cambia de pregunta. */
  preguntaId: string;
  onResolver: (opcion: string) => void;
};

/**
 * Árbol de sí/no que resuelve a un ancla del instrumento (NMS).
 *
 * Así administra Fundación el NMS: en vez de elegir entre cuatro anclas
 * matizadas en una pantalla, el paciente contesta hasta tres preguntas de sí/no.
 * Para el público de esta app la diferencia es grande — "¿caminás por tu casa?"
 * se contesta sin leer dos veces; "sí, con andador, bastón o muletas" vs "sí,
 * con la ayuda de otra persona" pide comparar.
 *
 * **El árbol es cómo se pregunta, no qué se registra.** Lo que sale de acá es el
 * código del ancla, que es lo que va al `QuestionnaireResponse` y lo que hace
 * comparable el puntaje con la literatura.
 *
 * Los íconos son de la librería y van `aria-hidden` (ADR-016): el nombre del
 * botón es su texto, que está a la vista.
 *
 * E3.8.2: la pregunta del nodo en serif, como el resto de las preguntas, y los
 * dos botones a la escala de las opciones (72 px).
 */
export function Arbol({ arbol, preguntaId, onResolver }: ArbolProps) {
  const [nodo, setNodo] = useState<NodoDeArbol>(arbol);

  // Cambiar de pregunta vuelve el árbol a su raíz. Sin esto, la pregunta
  // siguiente arrancaría a mitad del recorrido de la anterior.
  useEffect(() => {
    setNodo(arbol);
  }, [arbol]);

  function avanzar(siguiente: NodoDeArbol) {
    if (siguiente.clase === 'resultado') {
      setNodo(arbol);
      onResolver(siguiente.opcion);
      return;
    }
    setNodo(siguiente);
  }

  if (nodo.clase === 'resultado') return null;

  return (
    <fieldset className="flex flex-col gap-4 border-0 p-0" key={preguntaId}>
      <legend className="p-0 pb-5 font-serif text-3xl leading-tight font-regular text-heading">
        {nodo.texto}
      </legend>

      <div className="grid grid-cols-2 gap-3">
        <button
          className="flex min-h-18 items-center justify-center gap-2 rounded-lg bg-card px-5 text-xl font-bold text-heading outline-2 -outline-offset-2 outline-border hover:bg-brand-soft hover:outline-ring focus-visible:ring-3 focus-visible:ring-ring focus-visible:ring-offset-2"
          onClick={() => avanzar(nodo.si)}
          type="button"
        >
          <Check aria-hidden="true" className={`${ICONO.normal} text-accent-strong`} />
          {PACIENTE.formulario.si}
        </button>
        <button
          className="flex min-h-18 items-center justify-center gap-2 rounded-lg bg-card px-5 text-xl font-bold text-heading outline-2 -outline-offset-2 outline-border hover:bg-brand-soft hover:outline-ring focus-visible:ring-3 focus-visible:ring-ring focus-visible:ring-offset-2"
          onClick={() => avanzar(nodo.no)}
          type="button"
        >
          <X aria-hidden="true" className={`${ICONO.normal} text-subtle`} />
          {PACIENTE.formulario.no}
        </button>
      </div>
    </fieldset>
  );
}
